import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import {
  ChartOfAccountStyles,
  money,
  num,
} from "../Chart-Of-Account/ChartOfAccountShared";

const List = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await Http.get(`/journal-code`);
      setReceiveData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const summary = useMemo(() => {
    const rows = receiveData || [];
    return {
      count: rows.length,
      used: rows.filter((row) => num(row.debit) || num(row.credit)).length,
      debit: rows.reduce((sum, row) => sum + num(row.debit), 0),
      credit: rows.reduce((sum, row) => sum + num(row.credit), 0),
    };
  }, [receiveData]);

  const codeBodyTemplate = (rowData) => (
    <span className="coa-code">{rowData.code}</span>
  );

  const nameBodyTemplate = (rowData) => (
    <span style={{ fontWeight: 600, color: "#2a2962" }}>{rowData.name}</span>
  );

  const amountBodyTemplate = (field) => {
    const Component = (rowData) => {
      const value = num(rowData[field]);
      return (
        <span
          className="coa-num"
          style={{ display: "block", color: value ? "#35374d" : "#b6bacb" }}
        >
          {money(value)}
        </span>
      );
    };
    Component.displayName = `AmountBodyTemplate(${field})`;
    return Component;
  };

  // The model computes balance as debit minus credit.
  const balanceBodyTemplate = (rowData) => {
    const value = num(rowData.balance);
    return (
      <span
        className="coa-num"
        style={{
          display: "block",
          fontWeight: 700,
          color: value < 0 ? "#e42320" : "#2a2962",
        }}
      >
        {money(Math.abs(value))}
        <span className="coa-pill coa-pill-muted mg-l-6">
          {value < 0 ? "CR" : "DR"}
        </span>
      </span>
    );
  };

  const remarksBodyTemplate = (rowData) =>
    rowData.remarks ? (
      <span title={rowData.remarks}>{rowData.remarks}</span>
    ) : (
      <span style={{ color: "#b6bacb" }}>—</span>
    );

  const cols = [
    { field: "code", header: "Code", body: codeBodyTemplate },
    { field: "name", header: "Name", body: nameBodyTemplate },
    {
      field: "debit",
      header: "Debit",
      body: amountBodyTemplate("debit"),
      headerClassName: "coa-num",
    },
    {
      field: "credit",
      header: "Credit",
      body: amountBodyTemplate("credit"),
      headerClassName: "coa-num",
    },
    {
      field: "balance",
      header: "Balance",
      body: balanceBodyTemplate,
      headerClassName: "coa-num",
    },
    { field: "remarks", header: "Remarks", body: remarksBodyTemplate },
  ];

  const net = summary.debit - summary.credit;

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Journal Codes</h5>
            <p className="text-muted mb-0 tx-12">
              Codes that group journal vouchers, with what has been booked under
              each
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchData}
              title="Reload data"
            >
              <i className="fa fa-refresh"></i>
            </button>
            <Link to="/create-journal-code" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>New Journal Code
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="row mg-b-20">
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-hashtag coa-stat-icon"></i>
                <span className="coa-stat-label">Journal Codes</span>
                <span className="coa-stat-value">{summary.count}</span>
                <span className="coa-stat-sub">
                  {summary.used} with bookings
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-arrow-circle-o-down coa-stat-icon"></i>
                <span className="coa-stat-label">Total Debit</span>
                <span className="coa-stat-value">{money(summary.debit)}</span>
                <span className="coa-stat-sub">Across every code</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-arrow-circle-o-up coa-stat-icon"></i>
                <span className="coa-stat-label">Total Credit</span>
                <span className="coa-stat-value">{money(summary.credit)}</span>
                <span className="coa-stat-sub">Across every code</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div
                className={`coa-stat ${
                  Math.abs(net) < 0.005 ? "is-ok" : "is-alert"
                }`}
              >
                <i className="fa fa-balance-scale coa-stat-icon"></i>
                <span className="coa-stat-label">Difference</span>
                <span className="coa-stat-value">{money(Math.abs(net))}</span>
                <span className="coa-stat-sub">
                  {Math.abs(net) < 0.005
                    ? "Debits and credits agree"
                    : `Net ${net > 0 ? "debit" : "credit"} across all codes`}
                </span>
              </div>
            </div>
          </div>

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>All Journal Codes
              </span>
            </div>
            <div className="coa-panel-body">
              <ReactDataTable
                cols={cols}
                receiveData={receiveData}
                isLoading={loading}
                actionTemplate={actionTemplate}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const actionTemplate = (item) => {
  return (
    <div className="d-flex gap-1">
      <Link
        to={{ pathname: "/edit-journal-code/" + item.id }}
        className="btn btn-outline-secondary btn-sm"
        title="Edit journal code"
      >
        <i className="fa fa-pencil" aria-hidden="true"></i>
      </Link>
      <Link
        to={{ pathname: "/journal-code-ledger/" + item.id }}
        className="btn btn-outline-secondary btn-sm"
        title="View ledger"
      >
        <i className="fa fa-list" aria-hidden="true"></i>
      </Link>
    </div>
  );
};

export default List;
